import { useState } from 'react';

import { Loader } from '@/components/Loader';
import { Button } from '@/components/ui/button';
import { useAxios } from '@/hooks/useAxios';
import { Technology } from '@/types';
import { cn } from '@/utils/classname';

import { CpeEditor } from './CpeEditor';
import {
  DEFAULT_GOAL,
  invalidateOsintQueries,
  Pipeline,
} from './osintConstantineConstants';
import { PipelineSelect } from './PipelineSelect';

export type CpeStep = 'create-technology' | 'submit-scan';

interface CpeTabContentProps {
  onClose: () => void;
  onStepChange: (step: CpeStep, technology: Technology | null) => void;
}

const inputClasses = cn(
  'border-chariot-stroke bg-chariot-background-primary text-chariot-text-primary w-full rounded-md border px-3 py-2 text-sm',
  'focus:border-chariot-layer-primary focus:ring-chariot-layer-primary focus:ring-1 focus:outline-hidden',
  'disabled:cursor-not-allowed disabled:opacity-50'
);

export function CpeTabContent({ onClose, onStepChange }: CpeTabContentProps) {
  const axios = useAxios();

  // Step 1 state
  const [cpeStep, setCpeStep] = useState<CpeStep>('create-technology');
  const [cpe, setCpe] = useState('');
  const [technology, setTechnology] = useState<Technology | null>(null);
  const [isCreating, setIsCreating] = useState(false);

  // Step 2 state
  const [repoUrl, setRepoUrl] = useState('');
  const [goal, setGoal] = useState('');
  const [pipeline, setPipeline] = useState<Pipeline>('premium');
  const [isGuessing, setIsGuessing] = useState(false);
  const [isSubmitting, setIsSubmitting] = useState(false);

  // Shared error/success
  const [error, setError] = useState('');
  const [success, setSuccess] = useState('');

  const goToStepTwo = (tech: Technology) => {
    setTechnology(tech);
    setCpeStep('submit-scan');
    onStepChange('submit-scan', tech);
  };

  const goBackToStepOne = () => {
    setCpeStep('create-technology');
    setRepoUrl('');
    setGoal('');
    setPipeline('premium');
    setError('');
    onStepChange('create-technology', null);
  };

  const guessRepo = async (tech: Technology) => {
    setIsGuessing(true);
    setError('');
    try {
      const { data } = await axios.post('/osint/guess-repo', {
        cpe: tech.cpe,
        technology_name: tech.name || `${tech.vendor} ${tech.product}`,
      });
      setRepoUrl(data.repo_url || '');
    } catch (err) {
      console.error('Failed to guess repo:', err);
      setError('Could not guess repository URL. Please enter it manually.');
    } finally {
      setIsGuessing(false);
    }
  };

  const handleCreateTechnology = async () => {
    if (!cpe.trim()) {
      setError('CPE string is required');
      return;
    }

    setIsCreating(true);
    setError('');
    try {
      const { data } = await axios.post('/osint/create-technology', {
        cpe: cpe.trim(),
      });

      const createdTech = data.technology as Technology;
      goToStepTwo(createdTech);

      // Start guessing the repo URL
      guessRepo(createdTech);
    } catch (err) {
      const axiosErr = err as { response?: { data?: { message?: string } } };
      const message =
        axiosErr?.response?.data?.message ||
        'Failed to create technology. Check the CPE format.';
      setError(message);
    } finally {
      setIsCreating(false);
    }
  };

  const handleCpeSubmitScan = async () => {
    if (!repoUrl.trim()) {
      setError('Repository URL is required');
      return;
    }

    setIsSubmitting(true);
    setError('');
    try {
      await axios.post('/osint/submit', {
        repo_url: repoUrl.trim(),
        technology_key: technology?.key || undefined,
        goal: goal.trim() || DEFAULT_GOAL,
        pipeline,
      });

      invalidateOsintQueries();

      setSuccess(
        'Constantine scan initiated. Results will appear in the OSINT tab.'
      );
      setTimeout(() => {
        onClose();
      }, 2000);
    } catch (err) {
      const axiosErr = err as { response?: { data?: { message?: string } } };
      const message =
        axiosErr?.response?.data?.message ||
        'Failed to submit scan. Please try again.';
      setError(message);
    } finally {
      setIsSubmitting(false);
    }
  };

  const isStepOne = cpeStep === 'create-technology';
  const submitBusy = isSubmitting || !!success;

  return (
    <div className="space-y-4">
      {error && (
        <div
          className="bg-chariot-status-critical/10 text-chariot-status-critical rounded-md p-2 text-sm"
          role="alert"
        >
          {error}
        </div>
      )}

      {success && (
        <div
          className="bg-chariot-status-positive/10 text-chariot-status-positive rounded-md p-2 text-sm"
          role="status"
        >
          {success}
        </div>
      )}

      {isStepOne ? (
        <>
          <CpeEditor
            value={cpe}
            onChange={val => {
              setCpe(val);
              if (error) setError('');
            }}
            disabled={isCreating}
          />

          {/* Step 1 footer */}
          <div className="flex justify-between pt-2">
            <div />
            <div className="flex gap-2">
              <Button
                onClick={onClose}
                variant="ghost"
                className="bg-chariot-background-primary m-0! px-4"
                disabled={isCreating}
              >
                Cancel
              </Button>
              <Button
                onClick={handleCreateTechnology}
                disabled={!cpe.trim() || isCreating}
                className="ml-2 min-w-24 px-4"
              >
                {isCreating ? 'Creating...' : 'Next'}
              </Button>
            </div>
          </div>
        </>
      ) : (
        <>
          {/* Repository URL */}
          <div>
            <label
              htmlFor="osint-cpe-repo-url"
              className="text-chariot-text-primary mb-1 block text-sm font-medium"
            >
              Repository URL
            </label>
            <div className="relative">
              <input
                id="osint-cpe-repo-url"
                type="url"
                value={repoUrl}
                onChange={e => {
                  setRepoUrl(e.target.value);
                  if (error) setError('');
                }}
                placeholder={
                  isGuessing
                    ? 'Guessing repository...'
                    : 'https://github.com/org/repo'
                }
                disabled={isGuessing || submitBusy}
                className={cn(inputClasses, 'pr-9')}
              />
              {isGuessing && (
                <div className="absolute top-1/2 right-3 -translate-y-1/2">
                  <Loader isLoading type="spinner" />
                </div>
              )}
            </div>
          </div>

          {/* Run Tier */}
          <PipelineSelect
            value={pipeline}
            onValueChange={setPipeline}
            disabled={submitBusy}
          />

          {/* Research Goal */}
          <div>
            <label
              htmlFor="osint-cpe-goal"
              className="text-chariot-text-primary mb-1 block text-sm font-medium"
            >
              Research Goal
            </label>
            <textarea
              id="osint-cpe-goal"
              rows={8}
              value={goal}
              onChange={e => setGoal(e.target.value)}
              placeholder={DEFAULT_GOAL}
              disabled={submitBusy}
              className={cn(inputClasses, 'resize-y')}
            />
            <div className="mt-1 flex flex-col gap-1">
              {!goal.trim() && (
                <button
                  type="button"
                  onClick={() => setGoal(DEFAULT_GOAL)}
                  className="text-chariot-layer-primary self-start text-xs hover:underline"
                >
                  Use default prompt
                </button>
              )}
              <p className="text-chariot-text-secondary text-xs">
                {!goal.trim()
                  ? 'Describe what vulnerabilities to hunt for, or use the default prompt above.'
                  : goal === DEFAULT_GOAL
                    ? 'Default prompt targets critical 0-day vulnerabilities.'
                    : 'Your custom goal will focus the analysis.'}
              </p>
            </div>
          </div>

          {/* Step 2 footer */}
          <div className="flex justify-between pt-2">
            <button
              onClick={goBackToStepOne}
              className="text-chariot-text-secondary hover:text-chariot-text-primary text-sm"
              disabled={submitBusy}
            >
              Back
            </button>
            <div className="flex gap-2">
              <Button
                onClick={onClose}
                variant="ghost"
                className="bg-chariot-background-primary m-0! px-4"
                disabled={isSubmitting}
              >
                Cancel
              </Button>
              <Button
                onClick={handleCpeSubmitScan}
                disabled={!repoUrl.trim() || isGuessing || submitBusy}
                className="ml-2 min-w-24 px-4"
              >
                {isSubmitting ? 'Submitting...' : 'Start Scan'}
              </Button>
            </div>
          </div>
        </>
      )}
    </div>
  );
}
